import {
  createSlice,
  type ActionReducerMapBuilder,
  type AsyncThunk,
  type PayloadAction,
} from '@reduxjs/toolkit';

import {
  arquivarDocumentoPadrao,
  atualizarDocumentoPadrao,
  buscarPartesDocumentosPadrao,
  carregarMaisUsosDocumentoPadrao,
  carregarContextoDocumentosPadrao,
  carregarDetalheDocumentoPadrao,
  carregarDocumentosPadrao,
  criarDocumentoPadrao,
  duplicarDocumentoPadrao,
  excluirDocumentoPadrao,
  restaurarDocumentoPadrao,
  restaurarVersaoDocumentoPadrao,
  substituirArquivoDocumentoPadrao,
} from './actions';
import type {
  DocumentoPadrao,
  DocumentoPadraoParte,
  DocumentosPadraoAba,
  DocumentosPadraoContadores,
  DocumentosPadraoContexto,
  DocumentosPadraoFiltros,
  DocumentosPadraoPaginacao,
  DocumentosPadraoResumoValidade,
  ErroMutacaoDocumentoPadrao,
} from '@/features/documentosPadrao/types/documentosPadraoTypes';
import { FILTROS_DOCUMENTOS_PADRAO_VAZIOS } from '@/features/documentosPadrao/utils/documentosPadraoRegras';

type DocumentosPadraoStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/** Requisito do produto: 10 por página, server-side. */
export const ITENS_POR_PAGINA = 10;

const CONTADORES_VAZIOS: DocumentosPadraoContadores = {
  todos: 0,
  escritorio: 0,
  meusSetores: 0,
  pessoais: 0,
  arquivados: 0,
};

/**
 * O state guarda a PÁGINA corrente, não a coleção inteira: visibilidade, filtros, contadores e
 * paginação passaram a ser do servidor.
 */
interface DocumentosPadraoState {
  itens: DocumentoPadrao[];
  paginacao: DocumentosPadraoPaginacao;
  contadores: DocumentosPadraoContadores;
  resumoValidade: DocumentosPadraoResumoValidade;
  contexto: DocumentosPadraoContexto | null;
  referencia: string | null;
  diasAvisoVencimento: number | null;
  detalhe: DocumentoPadrao | null;
  /** Última página de usos já carregada no detalhe aberto. */
  paginaUsos: number;
  partes: DocumentoPadraoParte[];
  buscandoPartes: boolean;
  carregandoDetalhe: boolean;
  status: DocumentosPadraoStatus;
  erro: string | null;
  salvando: boolean;
  aba: DocumentosPadraoAba;
  filtros: DocumentosPadraoFiltros;
  /** Internal guards for in-flight request IDs to drop stale responses (race conditions). */
  _carregarRequestId?: string;
  _detalheRequestId?: string;
  _usosRequestId?: string;
  _partesRequestId?: string;
}

const initialState: DocumentosPadraoState = {
  itens: [],
  paginacao: { page: 1, limit: ITENS_POR_PAGINA, total: 0, totalPages: 1 },
  contadores: CONTADORES_VAZIOS,
  resumoValidade: { vencidos: 0, vencendo: 0 },
  contexto: null,
  referencia: null,
  diasAvisoVencimento: null,
  detalhe: null,
  paginaUsos: 1,
  partes: [],
  buscandoPartes: false,
  carregandoDetalhe: false,
  status: 'idle',
  erro: null,
  salvando: false,
  aba: 'todos',
  filtros: FILTROS_DOCUMENTOS_PADRAO_VAZIOS,
};

/**
 * Uniformiza pending/fulfilled/rejected das 7 mutações. O que mudou em relação ao mock: o
 * `fulfilled` NÃO remenda o array local — quem recarrega a página é o próprio thunk, senão
 * contadores e paginação passam a mentir.
 *
 * Mutação que falha NÃO escreve em `erro`: esse campo é o da LISTA e troca a biblioteca inteira
 * pelo `ErrorState`. A superfície de erro de mutação é o toast de `useDocumentosPadraoAcoes`
 * (review do PR #1141, COR-01 — os 409 de trava e de `revisao` derrubavam a tela).
 */
function registrarMutacao<Arg>(
  builder: ActionReducerMapBuilder<DocumentosPadraoState>,
  thunk: AsyncThunk<DocumentoPadrao, Arg, { rejectValue: ErroMutacaoDocumentoPadrao }>,
): void {
  builder
    .addCase(thunk.pending, (state) => {
      state.salvando = true;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state.salvando = false;
      // O detalhe aberto acompanha a mutação (versão nova, arquivamento, metadados).
      if (state.detalhe?.id === action.payload.id) state.detalhe = action.payload;
    })
    .addCase(thunk.rejected, (state) => {
      state.salvando = false;
    });
}

const documentosPadraoSlice = createSlice({
  name: 'documentosPadrao',
  initialState,
  reducers: {
    definirAbaDocumentosPadrao: (state, action: PayloadAction<DocumentosPadraoAba>) => {
      state.aba = action.payload;
      state.paginacao.page = 1;
    },
    definirFiltrosDocumentosPadrao: (
      state,
      action: PayloadAction<Partial<DocumentosPadraoFiltros>>,
    ) => {
      state.filtros = { ...state.filtros, ...action.payload };
      state.paginacao.page = 1;
    },
    limparFiltrosDocumentosPadrao: (state) => {
      state.filtros = FILTROS_DOCUMENTOS_PADRAO_VAZIOS;
      state.paginacao.page = 1;
    },
    definirPaginaDocumentosPadrao: (state, action: PayloadAction<number>) => {
      state.paginacao.page = action.payload;
    },
    fecharDetalheDocumentoPadrao: (state) => {
      state.detalhe = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(carregarDocumentosPadrao.pending, (state, action) => {
        state.status = 'loading';
        state.erro = null;
        state._carregarRequestId = action.meta.requestId;
      })
      .addCase(carregarDocumentosPadrao.fulfilled, (state, action) => {
        if (state._carregarRequestId && state._carregarRequestId !== action.meta.requestId) return;
        state.status = 'succeeded';
        state.itens = action.payload.itens;
        state.paginacao = action.payload.paginacao;
        state.contadores = action.payload.contadores;
        state.resumoValidade = action.payload.resumoValidade;
        state.referencia = action.payload.referencia;
        state.diasAvisoVencimento = action.payload.diasAvisoVencimento;
      })
      .addCase(carregarDocumentosPadrao.rejected, (state, action) => {
        if (state._carregarRequestId && state._carregarRequestId !== action.meta.requestId) return;
        state.status = 'failed';
        state.erro = action.payload ?? 'Não foi possível carregar os documentos padrão.';
      })
      .addCase(carregarContextoDocumentosPadrao.fulfilled, (state, action) => {
        state.contexto = action.payload;
      })
      .addCase(carregarDetalheDocumentoPadrao.pending, (state, action) => {
        state.carregandoDetalhe = true;
        state._detalheRequestId = action.meta.requestId;
      })
      .addCase(carregarDetalheDocumentoPadrao.fulfilled, (state, action) => {
        if (state._detalheRequestId && state._detalheRequestId !== action.meta.requestId) return;
        state.carregandoDetalhe = false;
        state.detalhe = action.payload;
        state.paginaUsos = 1;
      })
      .addCase(carregarDetalheDocumentoPadrao.rejected, (state, action) => {
        if (state._detalheRequestId && state._detalheRequestId !== action.meta.requestId) return;
        state.carregandoDetalhe = false;
      })
      .addCase(buscarPartesDocumentosPadrao.pending, (state, action) => {
        state.buscandoPartes = true;
        state._partesRequestId = action.meta.requestId;
      })
      .addCase(buscarPartesDocumentosPadrao.fulfilled, (state, action) => {
        if (state._partesRequestId !== action.meta.requestId) return;
        state.buscandoPartes = false;
        state.partes = action.payload;
      })
      .addCase(buscarPartesDocumentosPadrao.rejected, (state, action) => {
        if (state._partesRequestId !== action.meta.requestId) return;
        state.buscandoPartes = false;
      })
      .addCase(carregarMaisUsosDocumentoPadrao.pending, (state, action) => {
        state._usosRequestId = action.meta.requestId;
      })
      .addCase(carregarMaisUsosDocumentoPadrao.fulfilled, (state, action) => {
        if (state._usosRequestId && state._usosRequestId !== action.meta.requestId) return;
        const arg = action.meta.arg as { id: string; page: number };
        if (!state.detalhe || state.detalhe.id !== arg.id) return;
        state.paginaUsos += 1;
        const conhecidos = new Set(state.detalhe.usos.map((uso) => uso.id));
        state.detalhe.usos = [
          ...state.detalhe.usos,
          ...action.payload.filter((uso) => !conhecidos.has(uso.id)),
        ];
      })
      .addCase(excluirDocumentoPadrao.pending, (state) => {
        state.salvando = true;
      })
      .addCase(excluirDocumentoPadrao.fulfilled, (state, action) => {
        state.salvando = false;
        if (state.detalhe?.id === action.payload) state.detalhe = null;
      })
      .addCase(excluirDocumentoPadrao.rejected, (state) => {
        state.salvando = false;
      });

    registrarMutacao(builder, criarDocumentoPadrao);
    registrarMutacao(builder, atualizarDocumentoPadrao);
    registrarMutacao(builder, substituirArquivoDocumentoPadrao);
    registrarMutacao(builder, restaurarVersaoDocumentoPadrao);
    registrarMutacao(builder, duplicarDocumentoPadrao);
    registrarMutacao(builder, arquivarDocumentoPadrao);
    registrarMutacao(builder, restaurarDocumentoPadrao);
  },
});

export const {
  definirAbaDocumentosPadrao,
  definirFiltrosDocumentosPadrao,
  definirPaginaDocumentosPadrao,
  fecharDetalheDocumentoPadrao,
  limparFiltrosDocumentosPadrao,
} = documentosPadraoSlice.actions;

export const documentosPadraoReducer = documentosPadraoSlice.reducer;
